import { useState, useEffect } from 'react';
import { format, parseISO, isValid } from 'date-fns';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Employer cost default interface
interface EmployerCostDefault {
  setting_key: string;
  setting_value: number;
  description: string;
}

// Employee cost details interface
interface EmployeeCostDetails {
  id: string;
  employee_id: string;
  health_insurance_monthly: number;
  dental_monthly: number;
  vision_monthly: number;
  retirement_match_pct: number;
  other_benefits_monthly: number;
  pto_days_per_year: number;
  is_production_worker: boolean;
  workers_comp_override_pct: number | null;
  federal_payroll_override_pct: number | null;
  state_unemployment_override_pct: number | null;
  true_hourly_cost: number | null;
}

interface EmployeeCostDetailsWithCalc extends EmployeeCostDetails {
  employee_name: string;
  hourly_rate: number;
  department: string;
  calculation: {
    base_hourly_rate: number;
    true_hourly_cost: number;
    taxes_hourly: number;
    benefits_hourly: number;
    retirement_hourly: number;
    pto_hourly_cost: number;
  };
}

// US Tax Constants for 2026 (Texas has no state income tax)
const TAX_RATES = {
  SOCIAL_SECURITY: 0.062, // 6.2% on first $168,600 (2025 wage base)
  MEDICARE: 0.0145, // 1.45% on all wages
  ADDITIONAL_MEDICARE: 0.009, // 0.9% on wages over $200,000
  FEDERAL_UNEMPLOYMENT: 0.006, // 0.6% FUTA on first $7,000
  OVERTIME_MULTIPLIER: 1.5,
  STANDARD_WORK_WEEK: 40,
  SOCIAL_SECURITY_WAGE_BASE: 168600,
  ADDITIONAL_MEDICARE_THRESHOLD: 200000,
};

// Federal Tax Brackets 2026 (Single)
const FEDERAL_TAX_BRACKETS = [
  { min: 0, max: 11600, rate: 0.10 },
  { min: 11600, max: 47150, rate: 0.12 },
  { min: 47150, max: 100525, rate: 0.22 },
  { min: 100525, max: 191950, rate: 0.24 },
  { min: 191950, max: 243725, rate: 0.32 },
  { min: 243725, max: 609350, rate: 0.35 },
  { min: 609350, max: Infinity, rate: 0.37 },
];

// Employee classification types
type EmployeeClassification = 'w2_hourly' | 'w2_salary' | '1099_contractor' | '1099_commission' | '1099_mixed';

interface Employee {
  id: string;
  name: string;
  email: string;
  classification: EmployeeClassification; // W-2 or 1099 type
  pay_type: 'hourly' | 'salary' | 'commission' | 'mixed';
  pay_rate: number; // hourly rate or annual salary
  commission_rate?: number; // commission percentage for commission-based pay
  filing_status: 'single' | 'married' | 'head_of_household';
  allowances: number;
  benefits_deduction: number;
  retirement_percent: number;
  holiday_pay_eligible: boolean;
  ytd_gross: number;
  ytd_taxes: number;
  ytd_net: number;
  ytd_commission?: number; // track commission earnings separately
  ein_or_ssn_type: 'ssn' | 'ein'; // contractors may use EIN
}

interface PayPeriod {
  id: string;
  start_date: string;
  end_date: string;
  pay_date: string;
  status: 'draft' | 'processing' | 'completed' | 'paid';
  total_gross: number;
  total_taxes: number;
  total_net: number;
  employee_count: number;
}

interface PayStub {
  id: string;
  employee_id: string;
  employee_name: string;
  pay_period_id: string;
  hours_regular: number;
  hours_overtime: number;
  hours_holiday: number;
  gross_pay: number;
  federal_tax: number;
  social_security: number;
  medicare: number;
  benefits_deduction: number;
  retirement_deduction: number;
  other_deductions: number;
  net_pay: number;
  ytd_gross: number;
  ytd_taxes: number;
  ytd_net: number;
}

// Calculate federal tax withholding based on gross pay and filing status
const calculateFederalTax = (annualizedGross: number, filingStatus: string): number => {
  // Standard deduction for 2026
  const standardDeduction = filingStatus === 'married' ? 29200 : 14600;
  const taxableIncome = Math.max(0, annualizedGross - standardDeduction);

  let tax = 0;
  let brackets = FEDERAL_TAX_BRACKETS;
  if (filingStatus === 'married') {
    // Married brackets are roughly double
    brackets = brackets.map(b => ({ ...b, min: b.min * 2, max: b.max * 2 }));
  }

  for (const bracket of brackets) {
    if (taxableIncome > bracket.min) {
      const taxableInBracket = Math.min(taxableIncome, bracket.max) - bracket.min;
      tax += taxableInBracket * bracket.rate;
    }
  }

  return tax;
};

export function PayrollPage() {
  const [activeTab, setActiveTab] = useState<'employees' | 'employer_costs' | 'periods' | 'process'>('employees');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [payPeriods, setPayPeriods] = useState<PayPeriod[]>([]);
  // Per-employee printable time report
  const [trEmployee, setTrEmployee] = useState('');
  const [trMode, setTrMode] = useState<'period' | 'range'>('period');
  const [trPeriod, setTrPeriod] = useState('');
  const [trStart, setTrStart] = useState('');
  const [trEnd, setTrEnd] = useState('');
  const [trBusy, setTrBusy] = useState(false);
  const [trError, setTrError] = useState('');
  // Unused PTO report
  const [unusedPto, setUnusedPto] = useState<any[] | null>(null);
  const [unusedBusy, setUnusedBusy] = useState(false);

  const loadUnusedPto = async () => {
    setUnusedBusy(true);
    try {
      const token = localStorage.getItem('admin_auth_token');
      const res = await fetch(`${API_URL}/time-off/admin/unused-pto-report`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load');
      setUnusedPto(await res.json());
    } catch {
      setUnusedPto([]);
    } finally {
      setUnusedBusy(false);
    }
  };

  const exportUnusedPtoCsv = () => {
    if (!unusedPto || unusedPto.length === 0) return;
    const header = ['Employee', 'Hire date', 'PTO eligible', 'Allocated (hrs)', 'Used (hrs)', 'Remaining (hrs)'];
    const rows = unusedPto.map((r) => [
      `"${(r.name || '').replace(/"/g, '""')}"`, r.hireDate || '', r.ptoEligible ? 'yes' : 'no',
      r.allocatedHours, r.usedHours, r.remainingHours,
    ]);
    const csv = [header.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `unused-pto-${new Date().getFullYear()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const printTimeReport = async () => {
    setTrError('');
    if (!trEmployee) { setTrError('Select an employee'); return; }
    const params = new URLSearchParams({ employeeId: trEmployee });
    if (trMode === 'period') {
      if (!trPeriod) { setTrError('Select a pay period'); return; }
      params.set('periodId', trPeriod);
    } else {
      if (!trStart || !trEnd) { setTrError('Enter a start and end date'); return; }
      params.set('start', trStart);
      params.set('end', trEnd);
    }
    setTrBusy(true);
    // Open the tab synchronously so it isn't blocked as a pop-up, then fill it
    // once the authenticated PDF fetch resolves (bearer header can't ride a link).
    const win = window.open('', '_blank');
    try {
      const token = localStorage.getItem('admin_auth_token');
      const res = await fetch(`${API_URL}/payroll/time-report?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({ message: 'Failed to generate report' }));
        throw new Error(e.message || 'Failed to generate report');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (win) win.location.href = url; else window.open(url, '_blank');
    } catch (err: any) {
      if (win) win.close();
      setTrError(err.message || 'Failed to generate report');
    } finally {
      setTrBusy(false);
    }
  };
  const [loading, setLoading] = useState(true);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [processingPeriod, setProcessingPeriod] = useState<PayPeriod | null>(null);
  const [payStubs, setPayStubs] = useState<PayStub[]>([]);
  // No punches in a period is legitimate (a period before the time clock went
  // live) but was indistinguishable from a failed lookup — every hourly employee
  // silently showed 0 hours either way.
  const [punchRowsFound, setPunchRowsFound] = useState<number | null>(null);
  const [hoursLookupFailed, setHoursLookupFailed] = useState(false);
  const [showNewPeriodModal, setShowNewPeriodModal] = useState(false);
  const [newPeriodForm, setNewPeriodForm] = useState({ start_date: '', end_date: '', pay_date: '' });
  const [creatingPeriod, setCreatingPeriod] = useState(false);

  // Employer cost defaults state
  const [employerDefaults, setEmployerDefaults] = useState<EmployerCostDefault[]>([]);
  const [editingDefaults, setEditingDefaults] = useState<Record<string, number>>({});
  const [savingDefaults, setSavingDefaults] = useState(false);

  // Employee cost details state
  const [employeeCostDetails, setEmployeeCostDetails] = useState<EmployeeCostDetailsWithCalc | null>(null);
  const [editingCostDetails, setEditingCostDetails] = useState<Partial<EmployeeCostDetails>>({});

  useEffect(() => {
    loadData();
  }, []);

  const handleGenerateYear = async () => {
    const year = new Date().getFullYear();
    if (!confirm(`Create all weekly pay periods for ${year} (Thu→Wed, pay Fri)? Existing periods will be skipped.`)) return;
    try {
      const token = localStorage.getItem('admin_auth_token');
      const res = await fetch(`${API_URL}/payroll/periods/generate-year`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ year }),
      });
      if (!res.ok) {
        alert('Failed to generate periods');
        return;
      }
      const result = await res.json();
      alert(`Generated ${result.created} periods (${result.skipped} already existed)`);
      await loadData();
    } catch {
      alert('Failed to generate periods');
    }
  };

  const handleEditPeriod = async (periodId: string) => {
    const period = payPeriods.find((p) => p.id === periodId);
    if (!period) return;
    const newStart = prompt('Start date (YYYY-MM-DD):', period.start_date);
    if (!newStart) return;
    const newEnd = prompt('End date (YYYY-MM-DD):', period.end_date);
    if (!newEnd) return;
    const newPay = prompt('Pay date (YYYY-MM-DD):', period.pay_date);
    if (!newPay) return;
    try {
      const token = localStorage.getItem('admin_auth_token');
      const res = await fetch(`${API_URL}/payroll/periods/${periodId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ start_date: newStart, end_date: newEnd, pay_date: newPay }),
      });
      if (!res.ok) {
        alert('Failed to update period');
        return;
      }
      await loadData();
    } catch {
      alert('Failed to update period');
    }
  };

  const handleDeletePeriod = async (periodId: string) => {
    if (!confirm('Delete this pay period? Only draft periods can be deleted.')) return;
    try {
      const token = localStorage.getItem('admin_auth_token');
      const res = await fetch(`${API_URL}/payroll/periods/${periodId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Failed to delete' }));
        alert(err.message || 'Failed to delete period');
        return;
      }
      await loadData();
    } catch {
      alert('Failed to delete period');
    }
  };

  const handleCreatePeriod = async () => {
    if (!newPeriodForm.start_date || !newPeriodForm.end_date || !newPeriodForm.pay_date) {
      alert('Start date, end date, and pay date are all required');
      return;
    }
    setCreatingPeriod(true);
    try {
      const token = localStorage.getItem('admin_auth_token');
      const res = await fetch(`${API_URL}/payroll/periods`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newPeriodForm),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Failed to create period' }));
        alert(err.message || 'Failed to create period');
        return;
      }
      setShowNewPeriodModal(false);
      setNewPeriodForm({ start_date: '', end_date: '', pay_date: '' });
      await loadData();
    } finally {
      setCreatingPeriod(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_auth_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Load employer cost defaults
      try {
        const defaultsRes = await fetch(`${API_URL}/cost-accounting/employer-defaults`, { headers });
        if (defaultsRes.ok) {
          const defaults = await defaultsRes.json();
          setEmployerDefaults(defaults);
          // Initialize editing state
          const editMap: Record<string, number> = {};
          defaults.forEach((d: EmployerCostDefault) => {
            editMap[d.setting_key] = Number(d.setting_value);
          });
          setEditingDefaults(editMap);
        }
      } catch (e) {
        console.log('Cost accounting not available yet');
      }

      // Try to load from API
      try {
        const [empRes, periodsRes] = await Promise.all([
          fetch(`${API_URL}/payroll/employees`, { headers }),
          fetch(`${API_URL}/payroll/periods`, { headers }),
        ]);

        if (empRes.ok) setEmployees(await empRes.json());
        if (periodsRes.ok) setPayPeriods(await periodsRes.json());
      } catch {
        // Mock data for demo - includes W-2 and 1099 workers
        setEmployees([
          {
            id: 'emp-1',
            name: 'John Smith',
            email: 'john@kederscreens.com',
            classification: 'w2_hourly',
            pay_type: 'hourly',
            pay_rate: 22.50,
            filing_status: 'single',
            allowances: 1,
            benefits_deduction: 150,
            retirement_percent: 6,
            ytd_gross: 45000,
            ytd_taxes: 9500,
            ytd_net: 35500,
            ein_or_ssn_type: 'ssn',
          },
          {
            id: 'emp-2',
            name: 'Maria Garcia',
            email: 'maria@kederscreens.com',
            classification: 'w2_hourly',
            pay_type: 'hourly',
            pay_rate: 20.00,
            filing_status: 'married',
            allowances: 2,
            benefits_deduction: 200,
            retirement_percent: 4,
            ytd_gross: 40000,
            ytd_taxes: 7200,
            ytd_net: 32800,
            ein_or_ssn_type: 'ssn',
          },
          {
            id: 'emp-3',
            name: 'Mike Johnson',
            email: 'mike@kederscreens.com',
            classification: 'w2_salary',
            pay_type: 'salary',
            pay_rate: 65000, // annual salary
            filing_status: 'single',
            allowances: 1,
            benefits_deduction: 250,
            retirement_percent: 8,
            ytd_gross: 32500,
            ytd_taxes: 7800,
            ytd_net: 24700,
            ein_or_ssn_type: 'ssn',
          },
          {
            id: 'emp-4',
            name: 'Bob Wilson (1099)',
            email: 'bob@contractor.com',
            classification: '1099_contractor',
            pay_type: 'hourly',
            pay_rate: 35.00, // contractor hourly rate
            filing_status: 'single',
            allowances: 0,
            benefits_deduction: 0,
            retirement_percent: 0,
            ytd_gross: 28000,
            ytd_taxes: 0, // 1099 - no withholding
            ytd_net: 28000,
            ein_or_ssn_type: 'ssn',
          },
          {
            id: 'emp-5',
            name: 'Sarah Davis (1099+Comm)',
            email: 'sarah@sales.com',
            classification: '1099_commission',
            pay_type: 'commission',
            pay_rate: 0,
            commission_rate: 15, // 15% commission
            filing_status: 'single',
            allowances: 0,
            benefits_deduction: 0,
            retirement_percent: 0,
            ytd_gross: 52000,
            ytd_taxes: 0,
            ytd_net: 52000,
            ytd_commission: 52000,
            ein_or_ssn_type: 'ein', // uses business EIN
          },
        ]);

        setPayPeriods([
          {
            id: 'pp-1',
            start_date: '2026-01-06',
            end_date: '2026-01-12',
            pay_date: '2026-01-17',
            status: 'completed',
            total_gross: 12500,
            total_taxes: 2800,
            total_net: 9700,
            employee_count: 3,
          },
          {
            id: 'pp-2',
            start_date: '2026-01-13',
            end_date: '2026-01-19',
            pay_date: '2026-01-24',
            status: 'draft',
            total_gross: 0,
            total_taxes: 0,
            total_net: 0,
            employee_count: 3,
          },
        ]);
      }
    } catch (error) {
      console.error('Failed to load payroll data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePayStub = (
    employee: Employee,
    hours: { regular: number; overtime: number; holiday: number },
  ): PayStub => {
    // The regular/overtime split comes from the API, which buckets punches into
    // Mon-Sun weeks per the FLSA. Re-deriving it here as min(total, 40) would
    // disagree with the backend on any multi-week period, and would also treat
    // holiday hours as worked hours and push them into overtime.
    const regularHours = hours.regular;
    const overtimeHours = hours.overtime;
    const holidayHours = hours.holiday;

    let grossPay: number;
    if (employee.pay_type === 'hourly') {
      const regularPay = regularHours * employee.pay_rate;
      const overtimePay = overtimeHours * employee.pay_rate * TAX_RATES.OVERTIME_MULTIPLIER;
      // Holiday hours pay at straight time and never at the overtime multiplier.
      const holidayPay = holidayHours * employee.pay_rate;
      grossPay = regularPay + overtimePay + holidayPay;
    } else {
      // Salary - weekly pay (annual / 52)
      grossPay = employee.pay_rate / 52;
    }

    // Federal income tax by withholding method (one pay-period week).
    // Mirrors the server computeWithholding so stubs match the time report.
    const ew = employee as any;
    const is1099 = String(employee.classification || '').startsWith('1099');
    const method = ew.exempt_from_withholding ? 'none' : (ew.withholding_method || 'standard');
    let federalTax = 0;
    if (!is1099) {
      if (method === 'flat_amount') {
        federalTax = Number(ew.flat_withholding_amount) || 0;
      } else if (method === 'flat_percent') {
        federalTax = grossPay * ((Number(ew.flat_withholding_percent) || 0) / 100);
      } else if (method === 'none') {
        federalTax = 0;
      } else {
        federalTax = calculateFederalTax(grossPay * 52, employee.filing_status) / 52;
      }
      if (method !== 'none') federalTax += Number(ew.additional_withholding) || 0;
    }
    federalTax = Math.max(0, federalTax);

    // FICA — W-2 only (1099 contractors remit their own).
    const ytdWithCurrent = employee.ytd_gross + grossPay;
    let socialSecurity = 0;
    let medicare = 0;
    if (!is1099) {
      if (employee.ytd_gross < TAX_RATES.SOCIAL_SECURITY_WAGE_BASE) {
        const taxableForSS = Math.min(grossPay, TAX_RATES.SOCIAL_SECURITY_WAGE_BASE - employee.ytd_gross);
        socialSecurity = taxableForSS * TAX_RATES.SOCIAL_SECURITY;
      }
      medicare = grossPay * TAX_RATES.MEDICARE;
      if (ytdWithCurrent > TAX_RATES.ADDITIONAL_MEDICARE_THRESHOLD) {
        const additionalMedicareWages = Math.min(grossPay, ytdWithCurrent - TAX_RATES.ADDITIONAL_MEDICARE_THRESHOLD);
        medicare += additionalMedicareWages * TAX_RATES.ADDITIONAL_MEDICARE;
      }
    }

    // Deductions
    const retirementDeduction = grossPay * (employee.retirement_percent / 100);
    const benefitsDeduction = employee.benefits_deduction / 4; // Assuming monthly, divide by 4 for weekly

    const totalTaxes = federalTax + socialSecurity + medicare;
    const totalDeductions = benefitsDeduction + retirementDeduction;
    const netPay = grossPay - totalTaxes - totalDeductions;

    return {
      id: `stub-${employee.id}-${Date.now()}`,
      employee_id: employee.id,
      employee_name: employee.name,
      pay_period_id: processingPeriod?.id || '',
      hours_regular: regularHours,
      hours_overtime: overtimeHours,
      hours_holiday: holidayHours,
      gross_pay: Math.round(grossPay * 100) / 100,
      federal_tax: Math.round(federalTax * 100) / 100,
      social_security: Math.round(socialSecurity * 100) / 100,
      medicare: Math.round(medicare * 100) / 100,
      benefits_deduction: Math.round(benefitsDeduction * 100) / 100,
      retirement_deduction: Math.round(retirementDeduction * 100) / 100,
      other_deductions: 0,
      net_pay: Math.round(netPay * 100) / 100,
      ytd_gross: employee.ytd_gross + grossPay,
      ytd_taxes: employee.ytd_taxes + totalTaxes,
      ytd_net: employee.ytd_net + netPay,
    };
  };

  const handleProcessPayroll = async (period: PayPeriod) => {
    setProcessingPeriod(period);
    // Jump to the Process Payroll tab — the stubs render there, not on the Pay
    // Periods list. Without this, clicking Process appeared to do nothing.
    setActiveTab('process');
    setHoursLookupFailed(false);

    // Pull actual hours from CrossChex time_punches for this period
    let hoursByEmployee: Record<string, { regular: number; overtime: number; holiday: number }> = {};
    let punchRowCount = 0;
    try {
      const token = localStorage.getItem('admin_auth_token');
      const res = await fetch(`${API_URL}/payroll/periods/${period.id}/hours`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const rows: Array<{
          employee_id: string;
          hours_total: number;
          hours_regular: number;
          hours_overtime: number;
          hours_holiday: number;
        }> = await res.json();
        punchRowCount = rows.length;
        hoursByEmployee = Object.fromEntries(rows.map((r) => [r.employee_id, {
          regular: Number(r.hours_regular) || 0,
          overtime: Number(r.hours_overtime) || 0,
          holiday: Number(r.hours_holiday) || 0,
        }]));
      } else {
        setHoursLookupFailed(true);
      }
    } catch (err) {
      console.warn('Failed to auto-load period hours; defaulting to 0', err);
      setHoursLookupFailed(true);
    }
    setPunchRowsFound(punchRowCount);

    const ZERO = { regular: 0, overtime: 0, holiday: 0 };
    const stubs = employees.map((emp) => {
      // Hourly employees: pull from punches (0 if none). Salary: punches ignored,
      // and holidays too — a salaried week already includes them.
      const hours = emp.pay_type === 'hourly' ? (hoursByEmployee[emp.id] ?? ZERO) : ZERO;
      return calculatePayStub(emp, hours);
    });
    setPayStubs(stubs);
  };

  const handleSaveEmployee = async () => {
    if (!editingEmployee) return;
    try {
      const token = localStorage.getItem('admin_auth_token');

      // Save payroll settings
      const res = await fetch(`${API_URL}/payroll/employees/${editingEmployee.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editingEmployee),
      });

      // Save cost details (for W-2 employees)
      if (!editingEmployee.classification.startsWith('1099')) {
        await saveEmployeeCostDetails(editingEmployee.id);
      }

      if (res.ok) {
        setEditingEmployee(null);
        setEmployeeCostDetails(null);
        setEditingCostDetails({});
        loadData();
      }
    } catch {
      // Update locally for demo
      setEmployees(prev => prev.map(e => e.id === editingEmployee.id ? editingEmployee : e));

      // Still try to save cost details
      if (!editingEmployee.classification.startsWith('1099')) {
        await saveEmployeeCostDetails(editingEmployee.id);
      }

      setEditingEmployee(null);
      setEmployeeCostDetails(null);
      setEditingCostDetails({});
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const safeFormatDate = (dateStr: string, formatStr: string) => {
    try {
      // Calendar-date values arrive either as "YYYY-MM-DD" or as "YYYY-MM-DDT00:00:00.000Z"
      // (Postgres DATE → pg serializer). Both represent a calendar day, so anchor at noon
      // local time to avoid UTC-boundary date shift rendering Apr 16 as Apr 15 in CT.
      const match = dateStr?.match?.(/^(\d{4}-\d{2}-\d{2})/);
      const date = match ? new Date(`${match[1]}T12:00:00`) : parseISO(dateStr);
      if (!isValid(date)) return dateStr;
      return format(date, formatStr);
    } catch {
      return dateStr;
    }
  };

  // Save employer cost defaults
  const saveEmployerDefaults = async () => {
    setSavingDefaults(true);
    try {
      const token = localStorage.getItem('admin_auth_token');
      const defaults = Object.entries(editingDefaults).map(([setting_key, setting_value]) => ({
        setting_key,
        setting_value,
      }));

      const res = await fetch(`${API_URL}/cost-accounting/employer-defaults`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ defaults }),
      });

      if (res.ok) {
        const updated = await res.json();
        setEmployerDefaults(updated);
        alert('Employer cost defaults saved successfully!');
      } else {
        alert('Failed to save employer cost defaults');
      }
    } catch (error) {
      console.error('Failed to save employer defaults:', error);
      alert('Failed to save employer cost defaults');
    } finally {
      setSavingDefaults(false);
    }
  };

  // Load employee cost details when editing
  const loadEmployeeCostDetails = async (employeeId: string) => {
    try {
      const token = localStorage.getItem('admin_auth_token');
      const res = await fetch(`${API_URL}/cost-accounting/employee/${employeeId}/cost-details`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setEmployeeCostDetails(data);
        setEditingCostDetails({
          health_insurance_monthly: data.health_insurance_monthly || 0,
          dental_monthly: data.dental_monthly || 0,
          vision_monthly: data.vision_monthly || 0,
          retirement_match_pct: data.retirement_match_pct || 0,
          other_benefits_monthly: data.other_benefits_monthly || 0,
          pto_days_per_year: data.pto_days_per_year || 0,
          is_production_worker: data.is_production_worker || false,
          workers_comp_override_pct: data.workers_comp_override_pct,
          federal_payroll_override_pct: data.federal_payroll_override_pct,
          state_unemployment_override_pct: data.state_unemployment_override_pct,
        });
      }
    } catch (error) {
      console.log('Could not load cost details:', error);
    }
  };

  // Save employee cost details
  const saveEmployeeCostDetails = async (employeeId: string) => {
    try {
      const token = localStorage.getItem('admin_auth_token');
      const res = await fetch(`${API_URL}/cost-accounting/employee/${employeeId}/cost-details`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editingCostDetails),
      });
      if (res.ok) {
        const data = await res.json();
        setEmployeeCostDetails(data);
      }
    } catch (error) {
      console.error('Failed to save cost details:', error);
    }
  };

  // Format setting key to display name
  const formatSettingKey = (key: string): string => {
    return key
      .replace(/_pct$/, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading payroll data...</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Payroll Management</h1>
        <p className="text-gray-500">US/Texas payroll processing (Texas has no state income tax)</p>
      </div>

      {/* Per-employee time report (PDF) */}
      <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4 print:hidden">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Employee Time Report</h2>
        <p className="text-sm text-gray-500 mb-3">Printable per-employee timesheet (daily hours, regular, overtime, total) for the payroll file.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Employee</label>
            <select value={trEmployee} onChange={(e) => setTrEmployee(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[12rem]">
              <option value="">Select employee…</option>
              {[...employees].sort((a, b) => a.name.localeCompare(b.name)).map((emp) => (
                <option key={emp.id} value={emp.id}>{emp.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Range</label>
            <select value={trMode} onChange={(e) => setTrMode(e.target.value as 'period' | 'range')} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="period">Pay period</option>
              <option value="range">Custom dates</option>
            </select>
          </div>
          {trMode === 'period' ? (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Pay period</label>
              <select value={trPeriod} onChange={(e) => setTrPeriod(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[14rem]">
                <option value="">Select period…</option>
                {payPeriods.map((p) => (
                  <option key={p.id} value={p.id}>{p.start_date?.split('T')[0]} → {p.end_date?.split('T')[0]}</option>
                ))}
              </select>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Start</label>
                <input type="date" value={trStart} onChange={(e) => setTrStart(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">End</label>
                <input type="date" value={trEnd} onChange={(e) => setTrEnd(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            </>
          )}
          <button type="button" onClick={printTimeReport} disabled={trBusy} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:bg-gray-300">
            {trBusy ? 'Generating…' : 'Print / PDF'}
          </button>
        </div>
        {trError && <p className="text-sm text-red-600 mt-2">{trError}</p>}
      </div>

      {/* Unused PTO report */}
      <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4 print:hidden">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-gray-900">Unused PTO</h2>
          <div className="flex gap-2">
            <button type="button" onClick={loadUnusedPto} disabled={unusedBusy}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:bg-gray-300">
              {unusedBusy ? 'Loading…' : (unusedPto ? 'Refresh' : 'Load report')}
            </button>
            {unusedPto && unusedPto.length > 0 && (
              <button type="button" onClick={exportUnusedPtoCsv}
                className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium">Export CSV</button>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-3">Remaining PTO hours per employee. Unused hours are <strong>not carried past Jan 1</strong> (no rollover) — run this in December to pay out balances before the reset.</p>
        {unusedPto && (
          unusedPto.length === 0 ? (
            <p className="text-sm text-gray-400">No employees.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-1 pr-4">Employee</th>
                    <th className="py-1 pr-4">Hire date</th>
                    <th className="py-1 pr-4">PTO eligible</th>
                    <th className="py-1 pr-4 text-right">Allocated</th>
                    <th className="py-1 pr-4 text-right">Used</th>
                    <th className="py-1 pr-4 text-right">Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {unusedPto.map((r) => (
                    <tr key={r.employeeId} className="border-b last:border-0">
                      <td className="py-1 pr-4">{r.name}</td>
                      <td className="py-1 pr-4">{r.hireDate || '—'}</td>
                      <td className="py-1 pr-4">{r.ptoEligible ? 'Yes' : 'No'}</td>
                      <td className="py-1 pr-4 text-right">{Number(r.allocatedHours).toFixed(2)}</td>
                      <td className="py-1 pr-4 text-right">{Number(r.usedHours).toFixed(2)}</td>
                      <td className="py-1 pr-4 text-right font-medium">{Number(r.remainingHours).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6 print:hidden">
        <nav className="flex gap-4">
          {[
            { id: 'employees', label: 'Employee Setup' },
            { id: 'employer_costs', label: 'Employer Costs' },
            { id: 'periods', label: 'Pay Periods' },
            { id: 'process', label: 'Process Payroll' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Employee Setup Tab */}
      {activeTab === 'employees' && (
        <div className="space-y-4">
          {/* Classification Legend */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-2">Worker Classifications</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
              <div><span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">W-2 Hourly</span> Regular employee, hourly pay</div>
              <div><span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">W-2 Salary</span> Regular employee, salary</div>
              <div><span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">1099 Contractor</span> Independent contractor</div>
              <div><span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">1099 Commission</span> Commission-only contractor</div>
              <div><span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">1099 Mixed</span> Contractor with base + commission</div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="font-semibold text-gray-900">Employee & Contractor Pay Setup</h2>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                + Add Worker
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Worker</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Classification</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Pay Type</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Rate</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Tax ID</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Benefits</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">YTD Gross</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">YTD Tax</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {employees.map((emp) => {
                    const is1099 = emp.classification.startsWith('1099');
                    return (
                      <tr key={emp.id} className={`hover:bg-gray-50 ${is1099 ? 'bg-orange-50/30' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{emp.name}</div>
                          <div className="text-sm text-gray-500">{emp.email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            emp.classification === 'w2_hourly' ? 'bg-green-100 text-green-700' :
                            emp.classification === 'w2_salary' ? 'bg-green-100 text-green-700' :
                            emp.classification === '1099_contractor' ? 'bg-orange-100 text-orange-700' :
                            emp.classification === '1099_commission' ? 'bg-purple-100 text-purple-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {emp.classification.replace('_', ' ').replace('1099', '1099-NEC').toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 capitalize">{emp.pay_type}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                          {emp.pay_type === 'hourly' ? `${formatCurrency(emp.pay_rate)}/hr` :
                           emp.pay_type === 'salary' ? `${formatCurrency(emp.pay_rate)}/yr` :
                           emp.pay_type === 'commission' ? `${emp.commission_rate || 0}% comm` :
                           `${formatCurrency(emp.pay_rate)}/hr + ${emp.commission_rate || 0}%`}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {emp.ein_or_ssn_type === 'ein' ? 'EIN' : 'SSN'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {is1099 ? (
                            <span className="text-gray-400">N/A</span>
                          ) : (
                            `${formatCurrency(emp.benefits_deduction)}/mo`
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-green-600 font-medium">{formatCurrency(emp.ytd_gross)}</td>
                        <td className="px-4 py-3 text-sm font-medium">
                          {is1099 ? (
                            <span className="text-gray-400">$0 (1099)</span>
                          ) : (
                            <span className="text-red-600">{formatCurrency(emp.ytd_taxes)}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => {
                              setEditingEmployee(emp);
                              loadEmployeeCostDetails(emp.id);
                            }}
                            className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* 1099 Important Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-semibold text-yellow-800 mb-2">1099 Contractor Notice</h3>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• 1099 contractors do NOT have taxes withheld - they are responsible for their own tax payments</li>
              <li>• Form 1099-NEC must be issued to contractors who received $600+ in the tax year</li>
              <li>• Deadline: January 31 to provide to contractors, e-file with IRS by March 31</li>
              <li>• Keep contractor W-9 forms on file for tax reporting</li>
            </ul>
          </div>
        </div>
      )}

      {/* Employer Costs Tab */}
      {activeTab === 'employer_costs' && (
        <div className="space-y-6">
          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-2">Employer Cost Defaults</h3>
            <p className="text-sm text-blue-700">
              These default rates are used to calculate the true hourly cost of employees for manufacturing cost accounting.
              Individual employees can have override rates set in their profile if their costs differ from the defaults.
            </p>
          </div>

          {/* Employer Cost Defaults Table */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="font-semibold text-gray-900">Default Employer Tax & Insurance Rates</h2>
              <button
                onClick={saveEmployerDefaults}
                disabled={savingDefaults}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 text-sm"
              >
                {savingDefaults ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
            <div className="p-4">
              {employerDefaults.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  No employer cost defaults found. Run the database migration to set up cost accounting.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {employerDefaults.map((item) => (
                    <div key={item.setting_key} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {formatSettingKey(item.setting_key)}
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          step="0.01"
                          value={editingDefaults[item.setting_key] ?? item.setting_value}
                          onChange={(e) => setEditingDefaults({
                            ...editingDefaults,
                            [item.setting_key]: parseFloat(e.target.value) || 0,
                          })}
                          onWheel={(e) => e.currentTarget.blur()}
                          className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-right"
                        />
                        <span className="text-gray-600">%</span>
                      </div>
                      {item.description && (
                        <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* True Hourly Cost Formula Explanation */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-800 mb-2">True Hourly Cost Formula</h3>
            <div className="text-sm text-gray-600 space-y-2">
              <p className="font-mono bg-white p-2 rounded border border-gray-200">
                True Hourly Cost = (Hourly Rate × Tax Multiplier) + Benefits/hr + Retirement/hr + PTO Cost/hr
              </p>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                  <strong>Tax Multiplier:</strong>
                  <p className="text-xs mt-1">
                    1 + (Federal Payroll + State Unemployment + Federal Unemployment + Workers Comp + General Liability) / 100
                  </p>
                </div>
                <div>
                  <strong>Benefits Hourly:</strong>
                  <p className="text-xs mt-1">
                    (Health + Dental + Other Monthly Benefits) / 173.33 hours
                  </p>
                </div>
                <div>
                  <strong>Retirement Hourly:</strong>
                  <p className="text-xs mt-1">
                    Hourly Rate × (Employer Match % / 100)
                  </p>
                </div>
                <div>
                  <strong>PTO Cost Hourly:</strong>
                  <p className="text-xs mt-1">
                    (Hourly Rate × PTO Days × 8) / (2080 - PTO Days × 8)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Example Calculation */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Example Calculation</h3>
            <div className="grid grid-cols-2 gap-6 text-sm">
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Inputs:</h4>
                <ul className="space-y-1 text-gray-600">
                  <li>• Hourly Rate: $20.00</li>
                  <li>• Health Insurance: $500/month</li>
                  <li>• Dental/Vision: $50/month</li>
                  <li>• 401(k) Match: 4%</li>
                  <li>• PTO Days: 10</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Calculation:</h4>
                <ul className="space-y-1 text-gray-600">
                  <li>• Tax Multiplier: 1.1265 (12.65% total taxes)</li>
                  <li>• Base + Taxes: $20.00 × 1.1265 = $22.53</li>
                  <li>• Benefits: $550 / 173.33 = $3.17/hr</li>
                  <li>• Retirement: $20 × 4% = $0.80/hr</li>
                  <li>• PTO Cost: ($20 × 80) / 2000 = $0.80/hr</li>
                  <li className="font-semibold text-green-700 pt-2 border-t">
                    True Hourly Cost: $27.30/hr
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pay Periods Tab */}
      {activeTab === 'periods' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">
              Weekly schedule: Thursday start → Wednesday end → pay date same Wednesday (ACH posts overnight). New year periods auto-generate Jan 1.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleGenerateYear}
                className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
              >
                Generate {new Date().getFullYear()} Periods
              </button>
              <button
                onClick={() => setShowNewPeriodModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                + New Pay Period
              </button>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Period</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Pay Date</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Employees</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Total Gross</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Total Net</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payPeriods.map((period) => (
                  <tr key={period.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {safeFormatDate(period.start_date, 'MMM d')} - {safeFormatDate(period.end_date, 'MMM d, yyyy')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{safeFormatDate(period.pay_date, 'MMM d, yyyy')}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        period.status === 'completed' ? 'bg-green-100 text-green-700' :
                        period.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                        period.status === 'paid' ? 'bg-purple-100 text-purple-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {period.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{period.employee_count}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">{formatCurrency(period.total_gross)}</td>
                    <td className="px-4 py-3 text-sm text-green-600 font-medium">{formatCurrency(period.total_net)}</td>
                    <td className="px-4 py-3 space-x-3">
                      {period.status === 'draft' && (
                        <>
                          <button
                            onClick={() => handleProcessPayroll(period)}
                            className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                          >
                            Process
                          </button>
                          <button
                            onClick={() => handleEditPeriod(period.id)}
                            className="text-gray-600 hover:text-gray-800 font-medium text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeletePeriod(period.id)}
                            className="text-red-600 hover:text-red-800 font-medium text-sm"
                          >
                            Delete
                          </button>
                        </>
                      )}
                      {period.status === 'completed' && (
                        <button className="text-gray-600 hover:text-gray-800 font-medium text-sm">
                          View
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Process Payroll Tab */}
      {activeTab === 'process' && (
        <div className="space-y-6">
          {/* Tax Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-2">Texas Payroll Tax Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-blue-600">State Income Tax</div>
                <div className="font-bold text-blue-900">0% (None)</div>
              </div>
              <div>
                <div className="text-blue-600">Social Security</div>
                <div className="font-bold text-blue-900">6.2%</div>
              </div>
              <div>
                <div className="text-blue-600">Medicare</div>
                <div className="font-bold text-blue-900">1.45%</div>
              </div>
              <div>
                <div className="text-blue-600">Overtime Rate</div>
                <div className="font-bold text-blue-900">1.5x after 40 hrs</div>
              </div>
            </div>
          </div>

          {processingPeriod ? (
            <>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Processing: {safeFormatDate(processingPeriod.start_date, 'MMM d')} - {safeFormatDate(processingPeriod.end_date, 'MMM d, yyyy')}
                </h3>
                <div className="flex items-start justify-between gap-4">
                  <p className="text-gray-500 text-sm">Pay Date: {safeFormatDate(processingPeriod.pay_date, 'MMMM d, yyyy')}</p>
                  <button
                    onClick={() => window.print()}
                    className="print:hidden shrink-0 px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Print
                  </button>
                </div>

                {/* Say plainly where the hours came from. A period with no punches
                    is legitimate (it predates the time clock) but used to look
                    identical to a lookup that failed — both gave everyone 0 hours. */}
                {hoursLookupFailed ? (
                  <div className="mt-3 px-3 py-2 rounded bg-red-50 border border-red-200 text-sm text-red-700">
                    Could not load time punches for this period — every hourly employee is showing
                    0 hours. Enter hours manually or retry before processing.
                  </div>
                ) : punchRowsFound === 0 ? (
                  <div className="mt-3 px-3 py-2 rounded bg-amber-50 border border-amber-200 text-sm text-amber-800">
                    No time punches recorded in this period, so hourly employees show 0 hours.
                    Expected for periods before the time clock went live (first punch 15 Apr 2026).
                  </div>
                ) : punchRowsFound !== null ? (
                  <div className="mt-3 px-3 py-2 rounded bg-green-50 border border-green-200 text-sm text-green-800">
                    Hours loaded from time clock for {punchRowsFound} employee{punchRowsFound === 1 ? '' : 's'}.
                  </div>
                ) : null}
              </div>

              {/* Pay Stubs */}
              <div className="space-y-4">
                {payStubs.map((stub) => (
                  <div key={stub.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                      <div>
                        <span className="font-semibold text-gray-900">{stub.employee_name}</span>
                        <span className="text-gray-500 ml-2 text-sm">
                          {stub.hours_regular > 0 ? `${stub.hours_regular} hrs` : 'Salary'}
                          {stub.hours_overtime > 0 && ` + ${stub.hours_overtime} OT`}
                          {stub.hours_holiday > 0 && ` + ${stub.hours_holiday} holiday`}
                        </span>
                      </div>
                      <div className="text-lg font-bold text-green-600">
                        Net: {formatCurrency(stub.net_pay)}
                      </div>
                    </div>
                    <div className="p-4 grid grid-cols-3 gap-6">
                      {/* Earnings */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Earnings</h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Gross Pay</span>
                            <span className="text-gray-900 font-medium">{formatCurrency(stub.gross_pay)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Taxes */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Taxes</h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Federal Tax</span>
                            <span className="text-red-600">-{formatCurrency(stub.federal_tax)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Social Security</span>
                            <span className="text-red-600">-{formatCurrency(stub.social_security)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Medicare</span>
                            <span className="text-red-600">-{formatCurrency(stub.medicare)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">State Tax (TX)</span>
                            <span className="text-gray-400">$0.00</span>
                          </div>
                        </div>
                      </div>

                      {/* Deductions */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Deductions</h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Benefits</span>
                            <span className="text-red-600">-{formatCurrency(stub.benefits_deduction)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">401(k)</span>
                            <span className="text-red-600">-{formatCurrency(stub.retirement_deduction)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* YTD Summary */}
                    <div className="bg-gray-50 px-4 py-2 border-t border-gray-200">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">YTD Gross: {formatCurrency(stub.ytd_gross)}</span>
                        <span className="text-gray-500">YTD Taxes: {formatCurrency(stub.ytd_taxes)}</span>
                        <span className="text-gray-500">YTD Net: {formatCurrency(stub.ytd_net)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary & Actions */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div>
                    <div className="text-sm text-gray-500">Total Gross</div>
                    <div className="text-xl font-bold text-gray-900">
                      {formatCurrency(payStubs.reduce((sum, s) => sum + s.gross_pay, 0))}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Total Taxes</div>
                    <div className="text-xl font-bold text-red-600">
                      {formatCurrency(payStubs.reduce((sum, s) => sum + s.federal_tax + s.social_security + s.medicare, 0))}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Total Deductions</div>
                    <div className="text-xl font-bold text-orange-600">
                      {formatCurrency(payStubs.reduce((sum, s) => sum + s.benefits_deduction + s.retirement_deduction, 0))}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Total Net Pay</div>
                    <div className="text-xl font-bold text-green-600">
                      {formatCurrency(payStubs.reduce((sum, s) => sum + s.net_pay, 0))}
                    </div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setProcessingPeriod(null)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                    Approve & Complete Payroll
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <p className="text-gray-500 mb-4">Select a pay period from the Pay Periods tab to process payroll</p>
              <button
                onClick={() => setActiveTab('periods')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Go to Pay Periods
              </button>
            </div>
          )}
        </div>
      )}

      {/* Edit Employee Modal */}
      {editingEmployee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Edit Pay Setup - {editingEmployee.name}</h2>

            <div className="space-y-4">
              {/* Worker Classification */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">Worker Classification *</label>
                <select
                  value={editingEmployee.classification}
                  onChange={(e) => {
                    const classification = e.target.value as EmployeeClassification;
                    const is1099 = classification.startsWith('1099');
                    setEditingEmployee({
                      ...editingEmployee,
                      classification,
                      // Reset benefits for 1099
                      benefits_deduction: is1099 ? 0 : editingEmployee.benefits_deduction,
                      retirement_percent: is1099 ? 0 : editingEmployee.retirement_percent,
                      // Set appropriate pay type
                      pay_type: classification === 'w2_hourly' ? 'hourly' :
                               classification === 'w2_salary' ? 'salary' :
                               classification === '1099_contractor' ? 'hourly' :
                               classification === '1099_commission' ? 'commission' : 'mixed',
                    });
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                >
                  <optgroup label="W-2 Employees (Tax Withheld)">
                    <option value="w2_hourly">W-2 Hourly Employee</option>
                    <option value="w2_salary">W-2 Salaried Employee</option>
                  </optgroup>
                  <optgroup label="1099 Independent Contractors (No Tax Withholding)">
                    <option value="1099_contractor">1099-NEC Contractor (Hourly/Project)</option>
                    <option value="1099_commission">1099-NEC Commission Only</option>
                    <option value="1099_mixed">1099-NEC Base + Commission</option>
                  </optgroup>
                </select>
                {editingEmployee.classification.startsWith('1099') && (
                  <p className="text-xs text-orange-600 mt-1">
                    1099 contractors are responsible for their own taxes. No withholding will occur.
                  </p>
                )}
              </div>

              {/* Paid company holidays — opt-in per employee */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!editingEmployee.holiday_pay_eligible}
                    onChange={(e) => setEditingEmployee({
                      ...editingEmployee,
                      holiday_pay_eligible: e.target.checked,
                    })}
                    className="mt-1 h-4 w-4"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-700">Paid company holidays</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Credits hours for each company holiday in the pay period, at straight time.
                      Holiday hours do not count toward the 40-hour overtime threshold. Holidays are
                      configured on the Company Calendar; a half day credits half the standard day.
                    </span>
                  </span>
                </label>
              </div>

              {/* Tax ID Type (for 1099) */}
              {editingEmployee.classification.startsWith('1099') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID Type</label>
                  <select
                    value={editingEmployee.ein_or_ssn_type}
                    onChange={(e) => setEditingEmployee({ ...editingEmployee, ein_or_ssn_type: e.target.value as any })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="ssn">Social Security Number (SSN)</option>
                    <option value="ein">Employer Identification Number (EIN)</option>
                  </select>
                </div>
              )}

              {/* Pay Rate Section */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pay Type</label>
                  <select
                    value={editingEmployee.pay_type}
                    onChange={(e) => setEditingEmployee({ ...editingEmployee, pay_type: e.target.value as any })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    disabled={!editingEmployee.classification.startsWith('1099')}
                  >
                    <option value="hourly">Hourly</option>
                    <option value="salary">Salary</option>
                    {editingEmployee.classification.startsWith('1099') && (
                      <>
                        <option value="commission">Commission Only</option>
                        <option value="mixed">Base + Commission</option>
                      </>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {editingEmployee.pay_type === 'hourly' ? 'Hourly Rate' :
                     editingEmployee.pay_type === 'salary' ? 'Annual Salary' :
                     editingEmployee.pay_type === 'commission' ? 'Base (if any)' : 'Hourly Base Rate'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingEmployee.pay_rate}
                    onChange={(e) => setEditingEmployee({ ...editingEmployee, pay_rate: parseFloat(e.target.value) || 0 })}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    disabled={editingEmployee.pay_type === 'commission'}
                  />
                </div>
              </div>

              {/* Commission Rate (for commission-based) */}
              {(editingEmployee.pay_type === 'commission' || editingEmployee.pay_type === 'mixed') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Commission Rate (%)</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    value={editingEmployee.commission_rate || 0}
                    onChange={(e) => setEditingEmployee({ ...editingEmployee, commission_rate: parseFloat(e.target.value) })}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="e.g., 10 for 10%"
                  />
                  <p className="text-xs text-gray-500 mt-1">Percentage of sales/revenue paid as commission</p>
                </div>
              )}

              {/* W-2 Only: Tax Withholding */}
              {!editingEmployee.classification.startsWith('1099') && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Filing Status</label>
                      <select
                        value={editingEmployee.filing_status}
                        onChange={(e) => setEditingEmployee({ ...editingEmployee, filing_status: e.target.value as any })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      >
                        <option value="single">Single</option>
                        <option value="married">Married Filing Jointly</option>
                        <option value="head_of_household">Head of Household</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Allowances (W-4)</label>
                      <input
                        type="number"
                        value={editingEmployee.allowances}
                        onChange={(e) => setEditingEmployee({ ...editingEmployee, allowances: parseInt(e.target.value) })}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Benefits Deduction (Monthly)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={editingEmployee.benefits_deduction}
                        onChange={(e) => setEditingEmployee({ ...editingEmployee, benefits_deduction: parseFloat(e.target.value) })}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">401(k) Contribution %</label>
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        max="100"
                        value={editingEmployee.retirement_percent}
                        onChange={(e) => setEditingEmployee({ ...editingEmployee, retirement_percent: parseFloat(e.target.value) })}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                  </div>

                  {/* Withholding method */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Withholding Method</label>
                      <select
                        value={(editingEmployee as any).withholding_method || 'standard'}
                        onChange={(e) => setEditingEmployee({ ...editingEmployee, withholding_method: e.target.value } as any)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      >
                        <option value="standard">Standard (IRS equation)</option>
                        <option value="flat_amount">Flat $ amount</option>
                        <option value="flat_percent">Flat %</option>
                        <option value="none">None (defer to year-end)</option>
                      </select>
                    </div>
                    {(editingEmployee as any).withholding_method === 'flat_amount' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Flat Amount ($/paycheck)</label>
                        <input type="number" step="0.01"
                          value={(editingEmployee as any).flat_withholding_amount ?? 0}
                          onChange={(e) => setEditingEmployee({ ...editingEmployee, flat_withholding_amount: parseFloat(e.target.value) } as any)}
                          onWheel={(e) => e.currentTarget.blur()}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="e.g. 120.00" />
                      </div>
                    )}
                    {(editingEmployee as any).withholding_method === 'flat_percent' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Flat Percent (% of gross)</label>
                        <input type="number" step="0.1"
                          value={(editingEmployee as any).flat_withholding_percent ?? 0}
                          onChange={(e) => setEditingEmployee({ ...editingEmployee, flat_withholding_percent: parseFloat(e.target.value) } as any)}
                          onWheel={(e) => e.currentTarget.blur()}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="e.g. 18" />
                      </div>
                    )}
                  </div>
                  {(editingEmployee as any).withholding_method !== 'none' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Additional Withholding ($/paycheck)</label>
                        <input type="number" step="0.01"
                          value={(editingEmployee as any).additional_withholding ?? 0}
                          onChange={(e) => setEditingEmployee({ ...editingEmployee, additional_withholding: parseFloat(e.target.value) } as any)}
                          onWheel={(e) => e.currentTarget.blur()}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="extra on top, e.g. 10.00" />
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* 1099 Notice */}
              {editingEmployee.classification.startsWith('1099') && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <p className="text-sm text-orange-800">
                    <strong>1099 Contractor:</strong> No taxes will be withheld. Contractor is responsible for self-employment tax (15.3% FICA) and estimated quarterly payments.
                  </p>
                </div>
              )}

              {/* Cost Accounting Section (for W-2 employees) */}
              {!editingEmployee.classification.startsWith('1099') && (
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="text-green-600">$</span>
                    Cost Accounting Details
                  </h3>
                  <p className="text-xs text-gray-500 mb-3">
                    Used to calculate true hourly cost for manufacturing cost analysis
                  </p>

                  {/* Production Worker Toggle */}
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingCostDetails.is_production_worker || false}
                        onChange={(e) => setEditingCostDetails({
                          ...editingCostDetails,
                          is_production_worker: e.target.checked,
                        })}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Production Worker</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 ml-6">
                      Mark if this employee works on the production floor (affects overhead allocation)
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Health Insurance (Monthly)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editingCostDetails.health_insurance_monthly || 0}
                        onChange={(e) => setEditingCostDetails({
                          ...editingCostDetails,
                          health_insurance_monthly: parseFloat(e.target.value) || 0,
                        })}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        placeholder="0.00"
                      />
                      <p className="text-xs text-gray-500 mt-1">Employer-paid portion</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Dental (Monthly)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editingCostDetails.dental_monthly || 0}
                        onChange={(e) => setEditingCostDetails({
                          ...editingCostDetails,
                          dental_monthly: parseFloat(e.target.value) || 0,
                        })}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Vision (Monthly)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editingCostDetails.vision_monthly || 0}
                        onChange={(e) => setEditingCostDetails({
                          ...editingCostDetails,
                          vision_monthly: parseFloat(e.target.value) || 0,
                        })}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        401(k) Match (%)
                      </label>
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        max="100"
                        value={editingCostDetails.retirement_match_pct || 0}
                        onChange={(e) => setEditingCostDetails({
                          ...editingCostDetails,
                          retirement_match_pct: parseFloat(e.target.value) || 0,
                        })}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        placeholder="0"
                      />
                      <p className="text-xs text-gray-500 mt-1">Employer match percentage</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Other Benefits (Monthly)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editingCostDetails.other_benefits_monthly || 0}
                        onChange={(e) => setEditingCostDetails({
                          ...editingCostDetails,
                          other_benefits_monthly: parseFloat(e.target.value) || 0,
                        })}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        PTO Days (Per Year)
                      </label>
                      <input
                        type="number"
                        step="1"
                        min="0"
                        value={editingCostDetails.pto_days_per_year || 0}
                        onChange={(e) => setEditingCostDetails({
                          ...editingCostDetails,
                          pto_days_per_year: parseInt(e.target.value) || 0,
                        })}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        placeholder="0"
                      />
                      <p className="text-xs text-gray-500 mt-1">Vacation + sick days</p>
                    </div>
                  </div>

                  {/* Override Rates (collapsible) */}
                  <details className="mt-4">
                    <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-800">
                      Advanced: Override Tax Rates (click to expand)
                    </summary>
                    <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-xs text-yellow-700 mb-3">
                        Leave blank to use employer defaults. Override only if this employee has different rates.
                      </p>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Workers Comp %
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editingCostDetails.workers_comp_override_pct ?? ''}
                            onChange={(e) => setEditingCostDetails({
                              ...editingCostDetails,
                              workers_comp_override_pct: e.target.value ? parseFloat(e.target.value) : null,
                            })}
                            onWheel={(e) => e.currentTarget.blur()}
                            className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
                            placeholder="Default"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Federal Payroll %
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editingCostDetails.federal_payroll_override_pct ?? ''}
                            onChange={(e) => setEditingCostDetails({
                              ...editingCostDetails,
                              federal_payroll_override_pct: e.target.value ? parseFloat(e.target.value) : null,
                            })}
                            onWheel={(e) => e.currentTarget.blur()}
                            className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
                            placeholder="Default"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            State Unemp %
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editingCostDetails.state_unemployment_override_pct ?? ''}
                            onChange={(e) => setEditingCostDetails({
                              ...editingCostDetails,
                              state_unemployment_override_pct: e.target.value ? parseFloat(e.target.value) : null,
                            })}
                            onWheel={(e) => e.currentTarget.blur()}
                            className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
                            placeholder="Default"
                          />
                        </div>
                      </div>
                    </div>
                  </details>

                  {/* True Hourly Cost Display */}
                  {employeeCostDetails && employeeCostDetails.calculation && (
                    <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-sm font-medium text-green-800">True Hourly Cost:</span>
                          <p className="text-xs text-green-600 mt-0.5">
                            Base: ${employeeCostDetails.hourly_rate?.toFixed(2)}/hr →
                            Taxes: +${employeeCostDetails.calculation.taxes_hourly?.toFixed(2)} |
                            Benefits: +${employeeCostDetails.calculation.benefits_hourly?.toFixed(2)} |
                            401k: +${employeeCostDetails.calculation.retirement_hourly?.toFixed(2)} |
                            PTO: +${employeeCostDetails.calculation.pto_hourly_cost?.toFixed(2)}
                          </p>
                        </div>
                        <div className="text-2xl font-bold text-green-700">
                          ${employeeCostDetails.calculation.true_hourly_cost?.toFixed(2)}/hr
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setEditingEmployee(null);
                  setEmployeeCostDetails(null);
                  setEditingCostDetails({});
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEmployee}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Pay Period Modal */}
      {showNewPeriodModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-1">New Pay Period</h2>
            <p className="text-sm text-gray-500 mb-6">
              Hours for hourly employees will be auto-computed from CrossChex punches when you click Process.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={newPeriodForm.start_date}
                  onChange={(e) => setNewPeriodForm({ ...newPeriodForm, start_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={newPeriodForm.end_date}
                  onChange={(e) => setNewPeriodForm({ ...newPeriodForm, end_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pay Date</label>
                <input
                  type="date"
                  value={newPeriodForm.pay_date}
                  onChange={(e) => setNewPeriodForm({ ...newPeriodForm, pay_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowNewPeriodModal(false);
                  setNewPeriodForm({ start_date: '', end_date: '', pay_date: '' });
                }}
                disabled={creatingPeriod}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePeriod}
                disabled={creatingPeriod}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {creatingPeriod ? 'Creating…' : 'Create Period'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
